import { useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronRight, Plus, RefreshCw, ShoppingCart, Trash2, Truck } from "lucide-react";
import { Button } from "../components/ui/Button";
import { EmptyState } from "../components/ui/EmptyState";
import { Input } from "../components/ui/Input";
import { ListCapNotice } from "../components/ui/ListCapNotice";
import { PageHeader } from "../components/ui/PageHeader";
import { PortalState } from "../components/ui/PortalState";
import { apiGet, apiPost, PortalApiError } from "../api/portal-client";
import { usePortalResource } from "../hooks/usePortalResource";
import { DASH, formatDay, formatEuro, formatQuantity, sumOrNull, toFiniteNumber } from "../lib/format";

interface OrderLine {
  id: string;
  productName: string;
  designation: string | null;
  quantity: string | number;
  unite: string | null;
  unitPrice: string | number | null;
  totalHt: string | number | null;
  note: string | null;
  gencode: string | null;
}

interface Order {
  id: string;
  orderNumber: string | null;
  orderDate: string;
  status: string;
  statusLabel: string;
  awaitingValidation: boolean;
  dateLivraisonSouhaitee: string | null;
  totalTtc: string | number | null;
  transporteur: string | null;
  note: string | null;
  lines: OrderLine[];
}

interface CatalogueItem {
  productId: string | null;
  productName: string;
  unit: string | null;
  priceHT: number | null;
  vatPct: number | null;
  source: string;
}

interface Catalogue {
  general: boolean;
  items: CatalogueItem[];
}

/** Ligne en cours de saisie dans le formulaire de nouvelle commande. */
interface DraftLine {
  productId: string | null;
  productName: string;
  unit: string | null;
  /** `null` = le catalogue ne donne pas de prix pour ce produit. */
  priceHT: number | null;
  quantity: number;
}

/** Plafond serveur de `/me/commandes` (portal-auth.routes.ts). */
const COMMANDES_CAP = 50;

/**
 * Mes commandes.
 *
 * W7-B — deux lectures indépendantes au lieu d'un `Promise.all` : un catalogue
 * mort n'efface plus la liste des commandes passées (le rejet commun faisait
 * basculer toute la page en erreur). Chacune porte son état, et « Aucune
 * commande pour l'instant » n'apparaît qu'après une lecture réussie.
 *
 * Le total estimé suit le patron `sumOrNull` : un prix catalogue absent donnait
 * « NaN € ». Il vaut désormais « — » — et la commande reste envoyable, car
 * c'est le fournisseur qui fixe le prix, pas le portail.
 */
export function OrdersPage() {
  const commandes = usePortalResource<Order[]>(() => apiGet<Order[]>("/me/commandes"));
  const catalogue = usePortalResource<Catalogue>(() =>
    apiGet<Catalogue>("/me/commandes/catalogue"),
  );

  const [orders, setOrders] = useState<Order[]>([]);
  const [notice, setNotice] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [reordering, setReordering] = useState(false);

  // Formulaire de nouvelle commande
  const [formOpen, setFormOpen] = useState(false);
  const [draft, setDraft] = useState<DraftLine[]>([]);
  const [selected, setSelected] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [deliveryDate, setDeliveryDate] = useState("");
  const [orderNote, setOrderNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (commandes.data) setOrders(commandes.data);
  }, [commandes.data]);

  async function loadOrders() {
    setOrders(await apiGet<Order[]>("/me/commandes"));
  }

  /**
   * Total estimé — `null` dès qu'un prix manque. Un total partiel serait pire
   * qu'aucun total : le client s'engagerait sur un montant sous-évalué.
   */
  const draftTotal = useMemo(
    () => sumOrNull(draft.map((l) => (l.priceHT == null ? null : l.priceHT * l.quantity))),
    [draft],
  );

  async function handleQuickReorder() {
    setReordering(true);
    setNotice(null);
    setActionError(null);
    try {
      await apiPost("/me/quick-reorder", {});
      await loadOrders();
      setNotice("Commande refaite — elle a été transmise à votre fournisseur.");
    } catch (e: unknown) {
      setActionError(e instanceof PortalApiError ? e.message : "Impossible de refaire la commande.");
    } finally {
      setReordering(false);
    }
  }

  function handleAddLine() {
    const item = catalogue.data?.items.find((i) => (i.productId ?? i.productName) === selected);
    const qty = Number(quantity.replace(",", "."));
    if (!item || !Number.isFinite(qty) || qty <= 0) {
      setFormError("Choisissez un produit et une quantité valide.");
      return;
    }
    setFormError(null);
    setDraft((prev) => {
      const key = item.productId ?? item.productName;
      const existing = prev.find((l) => (l.productId ?? l.productName) === key);
      if (existing) {
        return prev.map((l) =>
          (l.productId ?? l.productName) === key ? { ...l, quantity: l.quantity + qty } : l,
        );
      }
      return [
        ...prev,
        {
          productId: item.productId,
          productName: item.productName,
          unit: item.unit,
          priceHT: toFiniteNumber(item.priceHT),
          quantity: qty,
        },
      ];
    });
    setQuantity("1");
  }

  async function handleSubmitOrder() {
    if (draft.length === 0) {
      setFormError("Ajoutez au moins un produit.");
      return;
    }
    setSubmitting(true);
    setFormError(null);
    try {
      await apiPost("/me/commandes", {
        lines: draft.map((l) => ({
          productId: l.productId ?? undefined,
          productName: l.productName,
          quantity: l.quantity,
        })),
        note: orderNote || undefined,
        deliveryDate: deliveryDate || undefined,
      });
      setDraft([]);
      setOrderNote("");
      setDeliveryDate("");
      setFormOpen(false);
      await loadOrders();
      setNotice("Commande transmise — elle attend la validation de votre fournisseur.");
    } catch (e: unknown) {
      setFormError(e instanceof PortalApiError ? e.message : "Envoi impossible.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="max-w-4xl mx-auto px-4 py-6">
      <PageHeader
        title="Mes commandes"
        subtitle="Suivez vos commandes en cours et passez-en une nouvelle."
        actions={
          <>
            {orders.length > 0 && (
              <Button variant="ghost" onClick={handleQuickReorder} disabled={reordering}>
                <RefreshCw size={16} /> {reordering ? "En cours..." : "Refaire ma dernière commande"}
              </Button>
            )}
            <Button onClick={() => setFormOpen((v) => !v)}>
              <Plus size={16} /> Nouvelle commande
            </Button>
          </>
        }
      />

      {notice && (
        <p role="status" className="text-sm text-success mb-4">
          {notice}
        </p>
      )}
      {actionError && (
        <p role="alert" className="text-sm text-danger mb-4">
          {actionError}
        </p>
      )}

      {formOpen && (
        <section className="bg-card border border-border rounded-xl2 p-4 mb-6">
          <h2 className="font-semibold text-ink mb-3">Nouvelle commande</h2>

          {/* Le catalogue a sa PROPRE matrice d'états : sans lui, on ne sait pas
              si le fournisseur ne vend rien ou si la lecture a échoué. */}
          <PortalState
            status={catalogue.status}
            label="le catalogue de votre fournisseur"
            error={catalogue.error}
            onRetry={catalogue.reload}
          />

          {catalogue.status === "ready" && catalogue.data?.general && (
            <p className="text-xs text-muted mb-3">
              Prix issus du catalogue général de votre fournisseur (aucun tarif négocié enregistré).
            </p>
          )}

          {catalogue.status === "ready" && (catalogue.data?.items.length ?? 0) === 0 && (
            <p className="text-sm text-muted">
              Aucun produit disponible à la commande pour l'instant. Contactez votre fournisseur.
            </p>
          )}

          {catalogue.status === "ready" && (catalogue.data?.items.length ?? 0) > 0 && (
            <>
              <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
                <div className="flex flex-col gap-1 flex-1">
                  <label htmlFor="product" className="text-sm font-medium text-ink">
                    Produit
                  </label>
                  <select
                    id="product"
                    value={selected}
                    onChange={(e) => setSelected(e.target.value)}
                    className="w-full rounded-xl2 border border-border bg-white px-4 py-2.5 text-sm text-ink outline-none focus:border-terracotta focus:ring-2 focus:ring-terracotta/20"
                  >
                    <option value="">Choisir un produit</option>
                    {catalogue.data!.items.map((i) => (
                      <option key={i.productId ?? i.productName} value={i.productId ?? i.productName}>
                        {i.productName} — {formatEuro(i.priceHT)}
                        {i.unit ? ` / ${i.unit}` : ""}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="w-full sm:w-32">
                  <Input
                    label="Quantité"
                    name="quantity"
                    type="number"
                    min="0"
                    step="0.001"
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                  />
                </div>
                <Button variant="secondary" onClick={handleAddLine}>
                  <Plus size={16} /> Ajouter
                </Button>
              </div>

              {draft.length > 0 && (
                <ul className="mt-4 divide-y divide-border border border-border rounded-xl2 overflow-hidden">
                  {draft.map((l) => (
                    <li key={l.productId ?? l.productName} className="flex items-center gap-3 px-4 py-2 text-sm">
                      <span className="flex-1 text-ink truncate">{l.productName}</span>
                      <span className="text-muted">{formatQuantity(l.quantity, l.unit)}</span>
                      <span className="font-mono text-ink">
                        {l.priceHT == null ? DASH : formatEuro(l.priceHT * l.quantity)}
                      </span>
                      <button
                        type="button"
                        onClick={() =>
                          setDraft((prev) =>
                            prev.filter((x) => (x.productId ?? x.productName) !== (l.productId ?? l.productName)),
                          )
                        }
                        aria-label={`Retirer ${l.productName}`}
                        className="p-1 rounded-lg text-muted hover:text-danger"
                      >
                        <Trash2 size={16} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                <Input
                  label="Date de livraison souhaitée"
                  name="deliveryDate"
                  type="date"
                  value={deliveryDate}
                  onChange={(e) => setDeliveryDate(e.target.value)}
                />
                <Input
                  label="Précisions (facultatif)"
                  name="orderNote"
                  value={orderNote}
                  onChange={(e) => setOrderNote(e.target.value)}
                  placeholder="Horaire de livraison, contact sur place..."
                />
              </div>

              {formError && (
                <p role="alert" className="text-sm text-danger mt-3">
                  {formError}
                </p>
              )}

              <div className="flex items-center justify-between gap-3 mt-4">
                <p className="text-sm text-muted">
                  Total estimé HT :{" "}
                  <span data-testid="draft-total" className="font-mono text-ink">
                    {draftTotal == null ? DASH : formatEuro(draftTotal)}
                  </span>
                  {draftTotal == null && draft.length > 0 && (
                    <span className="block text-xs">
                      Un prix manque au catalogue : votre fournisseur le confirmera à la validation.
                    </span>
                  )}
                </p>
                <Button onClick={handleSubmitOrder} disabled={submitting || draft.length === 0}>
                  {submitting ? "Envoi..." : "Envoyer la commande"}
                </Button>
              </div>
            </>
          )}
        </section>
      )}

      <PortalState
        status={commandes.status}
        label="vos commandes"
        error={commandes.error}
        onRetry={commandes.reload}
      />

      {commandes.status === "ready" && orders.length === 0 && (
        <EmptyState
          icon={ShoppingCart}
          title="Aucune commande pour l'instant"
          description="Composez votre commande à partir du catalogue de votre fournisseur."
          action={{ label: "Passer ma première commande", onClick: () => setFormOpen(true) }}
        />
      )}

      {commandes.status === "ready" && orders.length > 0 && (
        <>
          <div className="flex flex-col gap-3">
            {orders.map((o) => {
              const open = expanded === o.id;
              return (
                <article key={o.id} className="bg-card border border-border rounded-xl2 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setExpanded(open ? null : o.id)}
                    aria-expanded={open}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-cream/60"
                  >
                    <span className="shrink-0 text-muted" aria-hidden>
                      {open ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block font-semibold text-sm text-ink truncate">
                        {o.orderNumber ?? o.id.slice(-8)}
                      </span>
                      <span className="block text-xs text-muted truncate">
                        {formatDay(o.orderDate)} — {o.lines.length} ligne{o.lines.length > 1 ? "s" : ""}
                        {o.dateLivraisonSouhaitee
                          ? ` — livraison souhaitée le ${formatDay(o.dateLivraisonSouhaitee)}`
                          : ""}
                      </span>
                    </span>
                    {o.awaitingValidation ? (
                      <span className="shrink-0 text-xs rounded-full px-2 py-0.5 bg-terracotta/10 text-terracotta">
                        En attente de validation
                      </span>
                    ) : (
                      <span className="shrink-0 text-xs text-muted">{o.statusLabel}</span>
                    )}
                    <span className="shrink-0 font-mono text-sm text-ink">{formatEuro(o.totalTtc)}</span>
                  </button>

                  {open && (
                    <div className="border-t border-border px-4 py-3">
                      {o.transporteur && (
                        <p className="flex items-center gap-1.5 text-xs text-muted mb-3">
                          <Truck size={14} /> Transporteur : {o.transporteur}
                        </p>
                      )}
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-xs text-muted">
                            <th className="text-left font-medium py-1">Produit</th>
                            <th className="text-left font-medium py-1">Gencode</th>
                            <th className="text-right font-medium py-1">Qté</th>
                            <th className="text-right font-medium py-1">Prix HT</th>
                          </tr>
                        </thead>
                        <tbody>
                          {o.lines.map((l) => (
                            <tr key={l.id} className="border-t border-border">
                              <td className="py-2 text-ink">{l.designation ?? l.productName}</td>
                              <td className="py-2 text-xs text-muted font-mono">{l.gencode ?? DASH}</td>
                              <td className="py-2 text-right text-muted">
                                {formatQuantity(l.quantity, l.unite)}
                              </td>
                              <td className="py-2 text-right font-mono text-ink">
                                {formatEuro(l.unitPrice)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {o.note && <p className="text-xs text-muted mt-3">{o.note}</p>}
                    </div>
                  )}
                </article>
              );
            })}
          </div>
          <ListCapNotice shown={orders.length} cap={COMMANDES_CAP} label="commandes" genre="f" />
        </>
      )}
    </main>
  );
}
